import React from 'react'
import { styled, FloraVariants, forwardRef } from '@grupoboticario/flora-react-system'
import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const DEFAULT_TAG = 'span'
const cssTransparent = 'transparent'
const tokenNonInteractiveAltPredominant = '$nonInteractiveAltPredominant'
const tokenNonInteractivePredominant = '$nonInteractivePredominant'
const tokenDisabledContent = '$disabledContent'
const tokenDisabledAltBackground = '$disabledAltBackground'
const tokenErrorStandard = '$error-standard'
const tokenErrorAlt = '$error-alt'
const tokenSuccessStandard = '$success-standard'
const tokenSuccessAlt = '$success-alt'
const tokenAlertStandard = '$alert-standard'
const tokenAlertAlt = '$alert-alt'

const StyledTag = styled(DEFAULT_TAG, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  appearance: 'none',
  borderWidth: '0',
  boxSizing: 'border-box',
  flexShrink: 0,
  verticalAlign: 'middle',
  outline: 'none',
  textDecoration: 'none',
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  gap: '$1',
  borderRadius: '$small',
  color: tokenNonInteractivePredominant,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  fontSize: '$restricted',
  lineHeight: '16px',
  minWidth: '$3',
  minHeight: 24,
  border: `1px solid ${cssTransparent}`,
  textTransform: 'unset',

  '&:focus': {
    boxShadow: '$outline',
  },

  '& svg': {
    size: 12,
    cursor: 'pointer',
    '&:hover': {
      background: '$disabled',
      borderRadius: '$pill',
    },
  },

  variants: {
    variant: {
      disabled: {},
      assorted9: {},
      error: {},
      success: {},
      alert: {},
      primary: {
        backgroundColor: '$actionableDefault',
        color: '$backgroundPrimary',
        fontWeight: 'bold',
      },
    },
    shape: {
      solid: {},
      ghost: {},
    },
    alt: {
      true: {},
      false: {},
    },
    size: {
      small: {
        maxHeight: 16,
        minHeight: 16,
        padding: '0 $1',
      },
      medium: {
        padding: '$1 $2',
      },
    },
  },
  compoundVariants: [
    // disabled
    {
      shape: 'solid',
      alt: 'false',
      variant: 'disabled',
      css: {
        backgroundColor: '$disabledBackground',
        color: tokenDisabledContent,
      },
    },
    {
      shape: 'ghost',
      alt: 'false',
      variant: 'disabled',
      css: {
        backgroundColor: cssTransparent,
        color: tokenDisabledContent,
        border: `1px solid ${tokenDisabledContent}`,
      },
    },
    // disabled alt
    {
      shape: 'solid',
      alt: 'true',
      variant: 'disabled',
      css: {
        backgroundColor: tokenDisabledAltBackground,
        color: '$disabledAltContent',
      },
    },
    {
      shape: 'ghost',
      alt: 'true',
      variant: 'disabled',
      css: {
        backgroundColor: cssTransparent,
        color: '$disabledAltContent',
        border: `1px solid ${tokenDisabledAltBackground}`,
      },
    },
    // assorted9
    {
      shape: 'solid',
      alt: 'false',
      variant: 'assorted9',
      css: {
        backgroundColor: '$9',
        color: tokenNonInteractiveAltPredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'false',
      variant: 'assorted9',
      css: {
        backgroundColor: cssTransparent,
        color: '$9',
        border: '1px solid $9',
      },
    },
    // assorted9 alt
    {
      shape: 'solid',
      alt: 'true',
      variant: 'assorted9',
      css: {
        backgroundColor: '$9',
        color: tokenNonInteractiveAltPredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'true',
      variant: 'assorted9',
      css: {
        backgroundColor: cssTransparent,
        color: '$9',
        border: '1px solid $9',
      },
    },
    // error
    {
      shape: 'solid',
      alt: 'false',
      variant: 'error',
      css: {
        backgroundColor: tokenErrorStandard,
        color: tokenNonInteractiveAltPredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'false',
      variant: 'error',
      css: {
        backgroundColor: cssTransparent,
        color: tokenErrorStandard,
        border: `1px solid ${tokenErrorStandard}`,
      },
    },
    // error alt
    {
      shape: 'solid',
      alt: 'true',
      variant: 'error',
      css: {
        backgroundColor: tokenErrorAlt,
        color: tokenNonInteractivePredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'true',
      variant: 'error',
      css: {
        backgroundColor: cssTransparent,
        color: tokenErrorAlt,
        border: `1px solid ${tokenErrorAlt}`,
      },
    },
    // success
    {
      shape: 'solid',
      alt: 'false',
      variant: 'success',
      css: {
        backgroundColor: tokenSuccessStandard,
        color: tokenNonInteractiveAltPredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'false',
      variant: 'success',
      css: {
        backgroundColor: cssTransparent,
        color: tokenSuccessStandard,
        border: `1px solid ${tokenSuccessStandard}`,
      },
    },
    // success alt
    {
      shape: 'solid',
      alt: 'true',
      variant: 'success',
      css: {
        backgroundColor: tokenSuccessAlt,
        color: tokenNonInteractivePredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'true',
      variant: 'success',
      css: {
        backgroundColor: cssTransparent,
        color: tokenSuccessAlt,
        border: `1px solid ${tokenSuccessAlt}`,
      },
    },
    // alert
    {
      shape: 'solid',
      alt: 'false',
      variant: 'alert',
      css: {
        backgroundColor: tokenAlertStandard,
        color: tokenNonInteractiveAltPredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'false',
      variant: 'alert',
      css: {
        backgroundColor: cssTransparent,
        color: tokenAlertStandard,
        border: `1px solid ${tokenAlertStandard}`,
      },
    },
    // alert
    {
      shape: 'solid',
      alt: 'true',
      variant: 'alert',
      css: {
        backgroundColor: tokenAlertAlt,
        color: tokenNonInteractivePredominant,
      },
    },
    {
      shape: 'ghost',
      alt: 'true',
      variant: 'alert',
      css: {
        backgroundColor: cssTransparent,
        color: tokenAlertAlt,
        border: `1px solid ${tokenAlertAlt}`,
      },
    },
  ],
  defaultVariants: {
    variant: 'disabled',
    shape: 'solid',
    alt: 'false',
    size: 'medium',
  },
})

export type TagProps = HTMLFloraProps<'span'> &
  FloraVariants<typeof StyledTag> & {
    /**
     * If added, the tag will show an icon before the tag's label.
     */
    leftIcon?: React.ReactElement

    /**
     * If added, the tag will show an icon after the tag's label.
     */
    rightIcon?: React.ReactElement

    /**
     * The accessible label for the icon. This label will be visually hidden but announced to screen
     * reader users, similar to `alt` text for `img` tags.
     */
    label?: string
  }

/**
 * @component
 * @name Tag
 * @description Tag component
 * @tutorial https://flora.gb.tech/docs/components/core/tag
 */
export const Tag = forwardRef<'span', TagProps>(
  ({ children, leftIcon, rightIcon, label = 'icon', ...props }, forwardedRef) => {
    return (
      <StyledTag {...props} data-flora="tag" ref={forwardedRef}>
        {leftIcon && <TagIcon as={leftIcon} label={label} />}
        {children}
        {rightIcon && <TagIcon as={rightIcon} label={label} />}
      </StyledTag>
    )
  },
)

type TagIconProps = {
  label: string
  as: React.ReactElement
}

const TagIcon = ({ label = 'icon', as }: TagIconProps) => (
  <AccessibleIcon label={label} data-flora="tag">
    {React.cloneElement(as)}
  </AccessibleIcon>
)
